/**
 * screens/registry.tsx
 * Maps every shell screen id → its component. AppShell looks the active route
 * up here. Built screens are wired directly; any not-yet-implemented id falls
 * back to a clearly-labelled placeholder so the shell always renders.
 */

import React from 'react';
import { View, Text } from 'react-native';
import type { ShellScreen } from '../navigation/types';
import { useTheme } from '../context/themeContext';
import { SettingsHeader } from '../components/platform/primitives';

import Overview from './shell/overview';
import OrganizationScreen from './settings/organization';
import TeamScreen from './settings/team';
import PlanScreen from './settings/plan';
import BillingScreen from './settings/billing';
import SecurityScreen from './settings/security';
import AuditScreen from './settings/audit';
import PrivacyScreen from './settings/privacy';
import AdminOverview from './admin/adminOverview';
import AdminApplications from './admin/adminApplications';
import AdminPlans from './admin/adminPlans';
import AdminOrganizations from './admin/adminOrganizations';
import AdminDeliveryFailures from './admin/adminDeliveryFailures';
import CustomerAccessScreen from './settings/customerAccess';
import AdminSigningKeys from './admin/adminSigningKeys';
import AdminBypassLedger from './admin/adminBypassLedger';
import AdminRealmMismatch from './admin/adminRealmMismatch';
import AdminAudiences from './admin/adminAudiences';
import AdminCustomerActivity from './admin/adminCustomerActivity';
import CrmScreen from './products/crm';
import CatalogScreen from './products/catalog';
import VoiceScreen from './products/voice';

const Placeholder: React.FC<{ title: string }> = ({ title }) => {
  const { theme } = useTheme();
  return (
    <View style={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title={title} sub="This screen is coming up next in the build." />
      <Text style={{ color: theme.colors.ink3 }}>Not yet implemented.</Text>
    </View>
  );
};

export const SCREENS: Record<string, React.ComponentType<any>> = {
  // Tenant settings
  home: Overview,
  org: OrganizationScreen,
  team: TeamScreen,
  plan: PlanScreen,
  billing: BillingScreen,
  security: SecurityScreen,
  audit: AuditScreen,
  privacy: PrivacyScreen,
  // UICC-006 N-1 — customer access. A screen of its own, never a section of
  // plan or billing: the customer figure must not share a surface with a seat
  // count.
  'customer-access': CustomerAccessScreen,
  // Admin console
  'admin-overview': AdminOverview,
  'admin-applications': AdminApplications,
  'admin-plans': AdminPlans,
  'admin-organizations': AdminOrganizations,
  'admin-delivery-failures': AdminDeliveryFailures,
  // UICC-006 N-2 … N-6 — Softknack internal.
  'admin-signing-keys': AdminSigningKeys,
  'admin-bypass-ledger': AdminBypassLedger,
  'admin-realm-mismatch': AdminRealmMismatch,
  'admin-audiences': AdminAudiences,
  'admin-customer-activity': AdminCustomerActivity,
  // Product surfaces
  crm: CrmScreen,
  catalog: CatalogScreen,
  voice: VoiceScreen,
};

export { Placeholder };
